import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    PropsWithChildren
} from 'react';
import interact from 'interactjs';
import { ResizableContainer, Container, Point } from './HBResizable.styles';

export interface Props {
    /** Starting dimensions for the resizable content; ignored if larger than containerWidth. */
    initialSize?: {
        width: number;
        height: number;
    };
    /** Maximum width boundary for the resizable element, typically the parent container's width. */
    containerWidth: number;
    /** Called when the user finishes a resize drag. Receives the interact.js event with the final rect dimensions. */
    onResizeStop(event: React.MouseEvent): void;
    /** Changing this value triggers a layout recalculation that re-measures dimensions and restores the previous size if it fits. */
    recalculateTrigger: unknown;
    /** When true, always shows corner resize handles without requiring user click to focus. */
    forceFocus?: boolean;
    /** When true, hides corner resize handles even when the element is focused. */
    forceNoPointsShown?: boolean;
    /** Called with true when a resize drag starts and false when it ends, allowing the parent to track resize state. */
    setIsResizingImageBlock?: (state: boolean) => void;
}

const HBResizable = (props: PropsWithChildren<Props>) => {
    const {
        children,
        onResizeStop,
        containerWidth,
        recalculateTrigger,
        forceFocus = false,
        forceNoPointsShown = false,
        setIsResizingImageBlock = () => {}
    } = props;
    const containerRef = useRef(null);
    const resizableElementRef = useRef(null);

    const [ratio, setRatio] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const [_isFocused, setIsFocused] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentSize, setCurrentSize] = useState(null);
    const [previousSize, setPreviousSize] = useState(null);
    const [containerMaxWidth, setContainerMaxWidth] = useState(null);
    const [didUseInitialSize, setDidUseInitialSize] = useState(false);
    const initialSize = useRef(props.initialSize);
    initialSize.current = props.initialSize;

    // Store currentSize in a ref to access latest value in the recalculate effect
    // without adding it as a dependency (which would cause an infinite loop)
    const currentSizeRef = useRef(currentSize);
    currentSizeRef.current = currentSize;

    const isFocused = forceFocus || _isFocused;

    useEffect(() => {
        setIsResizingImageBlock(isResizing);
    }, [isResizing, setIsResizingImageBlock]);

    const onResizeEnd = useCallback(
        e => {
            const { width, height } = e.rect;

            if (Math.floor(width) !== Math.floor(containerMaxWidth)) {
                setCurrentSize({ width, height });
            }

            e.target.style.width = 'auto';
            e.target.style.height = 'auto';
            setIsResizing(false);
            onResizeStop(e);
        },
        [containerMaxWidth, onResizeStop]
    );

    const initInteract = useCallback(() => {
        interact(resizableElementRef.current)
            .resizable({
                // resize from all edges and corners
                // A corner is defined as a meeting of two edges. So each point class appears in both
                // edges that apply to it
                edges: {
                    left: '.hbresizable-edge-top-left, .hbresizable-edge-bottom-left',
                    right: '.hbresizable-edge-top-right, .hbresizable-edge-bottom-right',
                    bottom: '.hbresizable-edge-bottom-right, .hbresizable-edge-bottom-left',
                    top: '.hbresizable-edge-top-left, .hbresizable-edge-top-right'
                },
                listeners: {
                    move(event) {
                        // update the element's style
                        const { target } = event;
                        target.style.width = `${event.rect.width}px`;
                        target.style.height = `${event.rect.height}px`;

                        setIsResizing(true);
                        setCurrentSize(null);
                    }
                },
                modifiers: [
                    // maximum size
                    interact.modifiers.restrictSize({
                        max: {
                            width: containerMaxWidth,
                            height: containerMaxWidth / ratio
                        }
                    }),
                    interact.modifiers.aspectRatio({
                        ratio
                    })
                ]
            })
            .on('resizeend', e => {
                onResizeEnd(e);
            });
    }, [containerMaxWidth, onResizeEnd, ratio]);

    // This effect handles layout recalculations (e.g., when column count changes).
    // It saves the current size as "previous" so it can be restored after recalculation,
    // then resets currentSize to null to trigger dimension re-measurement.
    // We use currentSizeRef to access the latest currentSize value without adding it
    // as a dependency (which would cause an infinite loop since this effect sets currentSize to null).
    useEffect(() => {
        setPreviousSize(currentSizeRef.current);
        if (currentSizeRef.current) {
            setIsVisible(false);
        }
        setCurrentSize(null);
    }, [recalculateTrigger]);

    useEffect(() => {
        if (!resizableElementRef) {
            return null;
        }
        if (!isFocused) {
            return interact(resizableElementRef.current).unset();
        }
        return initInteract();
    }, [isFocused, resizableElementRef, containerMaxWidth, initInteract]);

    useEffect(() => {
        setContainerMaxWidth(containerWidth);
    }, [containerWidth]);

    useEffect(() => {
        // Current size null means that the image takes all the container's place.
        // At this moment we want to know the container max size (the boundaries).
        if (!currentSize && !isResizing) {
            const { width, height } =
                resizableElementRef.current.getBoundingClientRect();
            if (width === 0 || height === 0) {
                return;
            }
            setRatio(width / height);
            setContainerMaxWidth(width);

            // Set the initial value
            if (!didUseInitialSize) {
                setDidUseInitialSize(true);
                if (initialSize.current && initialSize.current.width < width) {
                    setCurrentSize(initialSize.current);
                }
            } else if (previousSize && previousSize.width < width) {
                // Set the previous size only if the new container is bigger then the image.
                // Otherwise, the image will get the container size (null).
                setCurrentSize(previousSize);
            }

            setIsVisible(true);
        }
    }, [
        containerMaxWidth,
        currentSize,
        didUseInitialSize,
        isResizing,
        previousSize
    ]);

    useEffect(() => {
        // Clean previous size if it is already used
        if (
            currentSize &&
            previousSize &&
            currentSize.width === previousSize.width
        ) {
            setPreviousSize(null);
        }
    }, [currentSize, previousSize]);

    const handleClickOutside = useCallback(
        e => {
            if (!containerRef.current?.contains(e.target)) {
                setIsFocused(false);
                document.removeEventListener(
                    'mousedown',
                    handleClickOutside,
                    true
                );
            }
        },
        [containerRef]
    );

    const handleClickToFocus = useCallback(() => {
        setIsFocused(true);
        document.addEventListener('mousedown', handleClickOutside, true);
    }, [handleClickOutside]);

    return (
        <ResizableContainer
            currentSize={currentSize}
            containerWidth={containerWidth}
            isVisible={didUseInitialSize && isVisible}
            disableTouchAction={isFocused}
            onClick={handleClickToFocus}
            ref={containerRef}
        >
            <Container ref={resizableElementRef}>
                {children}
                {isFocused && !forceNoPointsShown && (
                    <>
                        <Point
                            verticalPosition="top"
                            horizontalPosition="left"
                            className="hbresizable-edge-top-left"
                        />
                        <Point
                            verticalPosition="top"
                            horizontalPosition="right"
                            className="hbresizable-edge-top-right"
                        />
                        <Point
                            verticalPosition="bottom"
                            horizontalPosition="right"
                            className="hbresizable-edge-bottom-right"
                        />
                        <Point
                            verticalPosition="bottom"
                            horizontalPosition="left"
                            className="hbresizable-edge-bottom-left"
                        />
                    </>
                )}
            </Container>
        </ResizableContainer>
    );
};

export default HBResizable;
